import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { Duration } from "luxon";
import React, { useRef, useState } from "react";

import type { FieldKind } from "@storyboard/dispatch-view/fieldKinds";
import { fieldKindFor } from "@storyboard/dispatch-view/fieldKinds";
import type { DispatchControlValues } from "@storyboard/dispatch-view/query";
import {
  acknowledgementRefGroups,
  labelForAcknowledgementRef,
  type RequiresAcknowledgementRef,
} from "@storyboard/dispatch-view/requiresAcknowledgement";
import type { DispatchControl, DispatchControlValue, DispatchField } from "@storyboard/dispatch-view/view";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import { ACKNOWLEDGEABLE_TMS_CATEGORIES } from "@/hooks/dispatch/acknowledgements";
import { entersSearchResults } from "@/hooks/dispatch/dispatchKeymap";
import type { FieldFilter } from "@/hooks/dispatch/useDispatchFieldOptions";
import type { ValueOption } from "@/hooks/dispatch/valueOptions";
import type { TagDefinition } from "@/types/driver";
import { brandPrimary, zinc } from "@/ui/tokens";
import { fieldLabel } from "./columns";
import { denseSelectSx } from "./denseSelect";
import { DISPATCH_COLOR_VALUES, FILTERED_ACTION_SX, FILTERED_TINT } from "./dispatchColors";
import { EditorActions, ValueChecklist } from "./ValueChecklist";

/** DOM id of the search input, so the keyboard layer can focus it. */
export const DISPATCH_SEARCH_INPUT_ID = "dispatchSearch";

const controlSx = {
  height: 32,
  borderRadius: 1,
  bgcolor: zinc[700],
  color: zinc[200],
  fontSize: "0.8125rem",
  "&:hover": { bgcolor: zinc[600] },
} as const;

/** The header buttons share one look: a muted pill that lights up while its popover is open or it holds a value. */
export const headerButtonSx = (active: boolean) =>
  ({
    height: 32,
    px: 1.25,
    gap: 0.75,
    borderRadius: 1,
    fontSize: "0.8125rem",
    bgcolor: active ? zinc[600] : zinc[700],
    color: active ? "white" : zinc[200],
    "&:hover": { bgcolor: zinc[600], color: "white" },
  }) as const;

/** The Filter button while anything hides rows: the brand tint, so it reads apart from hover and an open popover. */
const filteredButtonSx = {
  ...headerButtonSx(true),
  bgcolor: FILTERED_TINT,
  ...FILTERED_ACTION_SX,
} as const;

/** The Filter button while a search sets its filters aside: greyed and dashed, so the search reads as what is in force. */
const hiddenButtonSx = {
  ...headerButtonSx(false),
  bgcolor: zinc[800],
  color: zinc[500],
  border: `1px dashed ${zinc[600]}`,
} as const;

const menuSlotProps = {
  paper: { sx: { bgcolor: zinc[800], color: "white", border: `1px solid ${zinc[600]}`, maxHeight: 360 } },
} as const;

const selectSx = { ...denseSelectSx(32, 1.5), ...controlSx, minWidth: 160 } as const;

/** A values picker's button, shaped like the selects beside it: its text at the left, the arrow at the right edge. */
const pickerSx = { ...controlSx, minWidth: 160, justifyContent: "space-between", gap: 0.5, pl: 1.5, pr: 0.5 } as const;

/**
 * The free-text search over the query's search fields; lives in the header beside the bulk actions, outside the Filter
 * popover. A term sets every filter aside, so while it holds one the box takes the brand's filtered look in their place.
 */
export function DispatchSearchBox({
  fields,
  tagsBySlug,
  noun,
  search,
  onSearchChange,
  onEnterResults,
  inputRef,
}: {
  /** The fields the search scans, named in the box's placeholder and tooltip. */
  fields: readonly DispatchField[];
  tagsBySlug: ReadonlyMap<string, TagDefinition>;
  /** What a row is, singular: "driver" or "message". */
  noun: string;
  search: string;
  onSearchChange: (search: string) => void;
  /**
   * Moves the keyboard into the results, on a plain ↓ or Enter. The key is prevented first, so the page's own
   * shortcuts skip it and Enter never sends or acknowledges.
   */
  onEnterResults: () => void;
  inputRef?: React.Ref<HTMLInputElement>;
}) {
  const scanned = fields.map((field) => fieldLabel(field, tagsBySlug).toLowerCase()).join(", ");
  const prompt = scanned === "" ? `Search all ${noun}s` : `Search by ${scanned}`;
  // The filter trims the term, so a blank term sets nothing aside and must not look active.
  const searching = search.trim() !== "";
  return (
    <Box
      data-sb={TestIds.dispatch.search}
      sx={{
        ...controlSx,
        display: "flex",
        alignItems: "center",
        px: 1,
        width: 260,
        bgcolor: searching ? FILTERED_TINT : zinc[700],
        border: `1px solid ${searching ? brandPrimary[400] : "transparent"}`,
        "&:hover": {},
      }}
    >
      <SearchIcon sx={{ fontSize: 18, color: searching ? brandPrimary[200] : zinc[400], mr: 0.5 }} />
      <InputBase
        id={DISPATCH_SEARCH_INPUT_ID}
        inputRef={inputRef}
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        onKeyDown={(event) => {
          if (!entersSearchResults(event.nativeEvent)) return;
          event.preventDefault();
          onEnterResults();
        }}
        placeholder={prompt}
        inputProps={{ "aria-label": prompt, title: scanned ? `Searches ${scanned}` : undefined, autoComplete: "off" }}
        sx={{
          flex: 1,
          minWidth: 0,
          color: "white",
          fontSize: "0.8125rem",
          "& input, & input::placeholder": { textOverflow: "ellipsis" },
        }}
      />
      {searching && (
        <IconButton
          size="small"
          aria-label="Clear search"
          onClick={() => onSearchChange("")}
          sx={{ color: brandPrimary[200] }}
        >
          <ClearIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
}

/** A toggle control as an On/Off pill; `name` is the control's label, so the button is found by it. */
function ToggleControl({ name, active, onToggle }: { name: string; active: boolean; onToggle: () => void }) {
  return (
    <ButtonBase
      data-sb={TestIds.dispatch.filterToggle}
      aria-label={name}
      aria-pressed={active}
      onClick={onToggle}
      sx={{
        ...controlSx,
        px: 1.5,
        alignSelf: "flex-start",
        bgcolor: active ? "var(--color-brand-primary-600)" : zinc[700],
        color: active ? "white" : zinc[300],
        "&:hover": { bgcolor: active ? "var(--color-brand-primary-500)" : zinc[600], color: "white" },
      }}
    >
      {active ? "On" : "Off"}
    </ButtonBase>
  );
}

/** What a shut values picker reads: the label alone while empty, else its values, or how many past two. */
function pickerText(label: string, values: readonly string[], labelOf: (value: string) => string): string {
  if (values.length === 0) return label;
  if (values.length > 2) return `${label}: ${values.length} selected`;
  return `${label}: ${values.map(labelOf).join(", ")}`;
}

/**
 * The values editor a picker opens: the column header's checklist without (blank), then Apply and Clear.
 *
 * Its ticks change nothing until Apply sets the control to them; Clear empties the control, as clearing its chip in
 * the strip does. Either closes the editor, and its ticks start over from the control's values each time it opens.
 */
function ValuesPickerEditor({
  options,
  values,
  multiple,
  onChange,
  onClose,
}: {
  options: readonly ValueOption[];
  values: readonly string[];
  multiple: boolean;
  onChange: (values: string[]) => void;
  onClose: () => void;
}) {
  const [chosen, setChosen] = useState<ReadonlySet<string>>(() => new Set(values));
  return (
    <Box sx={{ p: 0.5 }}>
      <ValueChecklist options={options} chosen={chosen} onChosenChange={setChosen} multiple={multiple} autoFocus />
      <EditorActions
        canApply={chosen.size > 0 || values.length > 0}
        canClear={values.length > 0}
        onApply={() => {
          onChange([...chosen]);
          onClose();
        }}
        onClear={() => {
          onChange([]);
          onClose();
        }}
      />
    </Box>
  );
}

/**
 * A values control as a button that reads like a select and opens the values editor in a popover of its own.
 *
 * Shut, it shows the label alone while the control is empty, else "Label: a, b", or "Label: N selected" past two.
 * Escape or a click outside closes the editor and drops its ticks, leaving the Filter popover open.
 */
function ValuesPicker({
  label,
  options,
  values,
  multiple,
  onChange,
}: {
  label: string;
  options: readonly ValueOption[];
  values: readonly string[];
  multiple: boolean;
  onChange: (values: string[]) => void;
}) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  // The popover unmounts the editor once it has closed; the key also remounts one reopened inside the close transition.
  const [openings, setOpenings] = useState(0);
  // A value a view or a stale filter names but the data no longer carries reads as itself.
  const labelOf = (value: string) => options.find((option) => option.value === value)?.label ?? value;
  return (
    <>
      <ButtonBase
        ref={anchorRef}
        data-sb={TestIds.dispatch.filterSelect}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setOpenings((previous) => previous + 1);
          setOpen(true);
        }}
        sx={pickerSx}
      >
        <Typography
          component="span"
          noWrap
          sx={{ fontSize: "inherit", minWidth: 0, color: values.length === 0 ? zinc[400] : undefined }}
        >
          {pickerText(label, values, labelOf)}
        </Typography>
        <ArrowDropDownIcon sx={{ color: zinc[400], flex: "none" }} />
      </ButtonBase>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            "data-sb": TestIds.dispatch.filterValuesEditor,
            role: "dialog",
            "aria-label": label,
            sx: { width: 280, bgcolor: zinc[800], color: "white", border: `1px solid ${zinc[600]}`, mt: 0.25, p: 0.5 },
          } as React.ComponentProps<typeof Popover>["PaperProps"],
        }}
      >
        <ValuesPickerEditor
          key={openings}
          options={options}
          values={values}
          multiple={multiple}
          onChange={onChange}
          onClose={() => setOpen(false)}
        />
      </Popover>
    </>
  );
}

/**
 * An enum or boolean values control as a row of chips: every known value with its color and how many rows carry it,
 * each a toggle. The first-class fields get this treatment so a dispatcher sees the whole vocabulary at once.
 */
function ChipsControl({
  options,
  values,
  multiple,
  onChange,
}: {
  options: readonly ValueOption[];
  values: string[];
  multiple: boolean;
  onChange: (values: string[]) => void;
}) {
  const toggle = (value: string) => {
    if (values.includes(value)) return onChange(values.filter((candidate) => candidate !== value));
    return onChange(multiple ? [...values, value] : [value]);
  };
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }} role="group">
      {options.map((option) => {
        const active = values.includes(option.value);
        const color = option.color ? DISPATCH_COLOR_VALUES[option.color] : zinc[300];
        return (
          <ButtonBase
            key={option.value}
            data-sb={TestIds.dispatch.filterChip}
            aria-pressed={active}
            onClick={() => toggle(option.value)}
            sx={{
              ...controlSx,
              height: 26,
              px: 1,
              gap: 0.5,
              fontSize: "0.75rem",
              bgcolor: active ? zinc[600] : zinc[800],
              color: active ? "white" : zinc[300],
              border: `1px solid ${active ? color : zinc[700]}`,
              "&:hover": { bgcolor: zinc[600], color: "white" },
            }}
          >
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: color, flex: "none" }} />
            {option.label}
            <Box component="span" sx={{ color: zinc[500], fontVariantNumeric: "tabular-nums" }}>
              {option.count}
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
}

/**
 * What this person is asked to acknowledge: a picker over driver messages and the acknowledgeable TMS categories and
 * kinds, and the chosen entries as chips, each removable. Always shown, since every view asks for something; while the
 * person's list differs from the view's, a link hands it back.
 */
function RequiresAcknowledgementControl({
  include,
  overridden,
  onChange,
  onReset,
}: {
  include: readonly string[];
  overridden: boolean;
  onChange: (include: RequiresAcknowledgementRef[]) => void;
  onReset: () => void;
}) {
  const groups = acknowledgementRefGroups(ACKNOWLEDGEABLE_TMS_CATEGORIES, include);
  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const raw = event.target.value;
    onChange((Array.isArray(raw) ? raw : [raw]) as RequiresAcknowledgementRef[]);
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      <Select<string[]>
        data-sb={TestIds.dispatch.filterRequiresAcknowledgement}
        multiple
        displayEmpty
        value={[...include]}
        onChange={handleChange}
        renderValue={(selected) => (
          <Typography sx={{ fontSize: "inherit", color: selected.length === 0 ? zinc[400] : undefined }}>
            {selected.length === 0 ? "Nothing" : `${selected.length} selected`}
          </Typography>
        )}
        MenuProps={{ slotProps: menuSlotProps }}
        sx={selectSx}
      >
        {groups.flatMap((group) => [
          <ListSubheader
            key={`group:${group.group}`}
            sx={{ bgcolor: zinc[800], color: zinc[500], fontSize: "0.6875rem", lineHeight: "24px" }}
          >
            {group.group}
          </ListSubheader>,
          ...group.items.map((item) => (
            <MenuItem key={item.value} value={item.value} sx={{ fontSize: "0.8125rem", py: 0.25 }}>
              <Checkbox size="small" checked={include.includes(item.value)} sx={{ p: 0.5, mr: 0.5 }} />
              {item.label}
            </MenuItem>
          )),
        ])}
      </Select>
      {include.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
          {include.map((ref) => (
            <Box
              key={ref}
              data-sb={TestIds.dispatch.filterRequiresAcknowledgementChip}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                height: 22,
                pl: 1,
                pr: 0.25,
                borderRadius: 11,
                fontSize: "0.75rem",
                color: zinc[300],
                bgcolor: zinc[800],
                border: `1px solid ${zinc[700]}`,
              }}
            >
              {labelForAcknowledgementRef(ref)}
              <ButtonBase
                aria-label={`Remove ${labelForAcknowledgementRef(ref)}`}
                onClick={() => onChange(include.filter((other) => other !== ref) as RequiresAcknowledgementRef[])}
                sx={{ borderRadius: "50%", p: 0.25, color: zinc[500], "&:hover": { color: "white" } }}
              >
                <ClearIcon sx={{ fontSize: 14 }} />
              </ButtonBase>
            </Box>
          ))}
        </Box>
      )}
      {overridden && (
        <ButtonBase
          onClick={onReset}
          sx={{ alignSelf: "flex-start", fontSize: "0.75rem", color: zinc[500], "&:hover": { color: "white" } }}
        >
          Use the view&apos;s list
        </ButtonBase>
      )}
    </Box>
  );
}

/** The spans a time control offers when the view names none, in minutes. */
const DEFAULT_TIME_PRESETS = [
  { minutes: 15 },
  { minutes: 30 },
  { hours: 1 },
  { hours: 2 },
  { hours: 4 },
  { hours: 8 },
].map((span) => Duration.fromObject(span).as("minutes"));

const MINUTES_PER_DAY = Duration.fromObject({ days: 1 }).as("minutes");
const MINUTES_PER_HOUR = Duration.fromObject({ hours: 1 }).as("minutes");

function formatMinutes(minutes: number): string {
  if (minutes % MINUTES_PER_DAY === 0) return `${minutes / MINUTES_PER_DAY}d`;
  if (minutes % MINUTES_PER_HOUR === 0) return `${minutes / MINUTES_PER_HOUR}h`;
  return `${minutes}m`;
}

function TimeControl({
  label,
  presets,
  minutes,
  onChange,
}: {
  label: string;
  presets: readonly number[];
  minutes: number;
  onChange: (minutes: number) => void;
}) {
  const options = [...new Set([...presets, ...(minutes > 0 ? [minutes] : [])])].sort((a, b) => a - b);
  return (
    <Select
      data-sb={TestIds.dispatch.filterSelect}
      displayEmpty
      value={minutes > 0 ? String(minutes) : ""}
      onChange={(event) => onChange(Number(event.target.value) || 0)}
      renderValue={(selected) => (
        <Typography sx={{ fontSize: "inherit", color: selected === "" ? zinc[400] : undefined }}>
          {selected === "" ? label : `${label}: last ${formatMinutes(Number(selected))}`}
        </Typography>
      )}
      MenuProps={{ slotProps: menuSlotProps }}
      sx={selectSx}
    >
      <MenuItem value="" sx={{ fontSize: "0.8125rem", color: zinc[400] }}>
        Any time
      </MenuItem>
      {options.map((option) => (
        <MenuItem key={option} value={String(option)} sx={{ fontSize: "0.8125rem", py: 0.25 }}>
          Last {formatMinutes(option)}
        </MenuItem>
      ))}
    </Select>
  );
}

/** One control in the popover: its label above its editor. */
function ControlRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, letterSpacing: "0.04em", color: zinc[500] }}>
        {label.toUpperCase()}
      </Typography>
      {children}
    </Box>
  );
}

/**
 * A values editor for one field, by the field's kind: chips for an enum or a boolean, a picker that opens the values
 * editor for the rest.
 */
function FieldValuesEditor({
  label,
  kind,
  options,
  values,
  onChange,
}: {
  label: string;
  kind: FieldKind;
  options: readonly ValueOption[];
  values: string[];
  onChange: (values: string[]) => void;
}) {
  if (kind.valueType === "enum" || kind.valueType === "boolean") {
    return <ChipsControl options={options} values={values} multiple onChange={onChange} />;
  }
  return <ValuesPicker label={label} options={options} values={values} multiple onChange={onChange} />;
}

/** The filter menu's props; the popover body takes all of them but the badge count. */
type DispatchFilterMenuProps = {
  controls: readonly DispatchControl[];
  tagsBySlug: ReadonlyMap<string, TagDefinition>;
  /** Every control's value in force, by control id. */
  controlValues: DispatchControlValues;
  onControlChange: (id: string, value: DispatchControlValue) => void;
  /** The options a `values` control offers, with counts; enums list the whole registry. */
  optionsFor: (control: DispatchControl) => readonly ValueOption[];
  /** The fields with no column that get an editor here. */
  fieldFilters: readonly FieldFilter[];
  /** The options a field's editor offers, with counts. */
  fieldOptionsFor: (field: DispatchField) => readonly ValueOption[];
  /** The values the ad-hoc conditions on a field name right now. */
  chosenValuesFor: (field: DispatchField) => string[];
  /** Replaces the ad-hoc conditions on a field with the chosen values, or none. */
  onFieldValuesChange: (field: DispatchField, values: string[]) => void;
  /** The list in force, whether it is the person's own, and how to change it or hand it back to the view's. */
  requiresAcknowledgement: {
    include: readonly string[];
    overridden: boolean;
    onChange: (include: RequiresAcknowledgementRef[]) => void;
    onReset: () => void;
  };
  /** How many controls and ad-hoc conditions narrow the rows right now. */
  activeCount: number;
  /** Whether a search has set those filters aside, so the button says they are hidden rather than in force. */
  hidden: boolean;
};

/** The Filter button's look: hidden behind a search, filtering, or plain (lit while its popover is open). */
function filterButtonSx(activeCount: number, hidden: boolean, open: boolean) {
  if (activeCount === 0) return headerButtonSx(open);
  return hidden ? hiddenButtonSx : filteredButtonSx;
}

/**
 * The popover's controls, a component of its own so their options are built only while the popover is mounted: every
 * `optionsFor` call is an O(rows) pass, and the menu re-renders with every write to the rows.
 */
function FilterMenuBody({
  controls,
  tagsBySlug,
  controlValues,
  onControlChange,
  optionsFor,
  fieldFilters,
  fieldOptionsFor,
  chosenValuesFor,
  onFieldValuesChange,
  requiresAcknowledgement,
}: Omit<DispatchFilterMenuProps, "activeCount" | "hidden">) {
  return (
    <>
      <ControlRow label="Requires acknowledgement">
        <RequiresAcknowledgementControl {...requiresAcknowledgement} />
      </ControlRow>
      {controls.map((control) => {
        const key = control.id;
        const value = controlValues[key];
        if (control.kind === "toggle") {
          return (
            <ControlRow key={key} label={control.label}>
              <ToggleControl
                name={control.label}
                active={value === true}
                onToggle={() => onControlChange(key, value !== true)}
              />
            </ControlRow>
          );
        }
        const label = control.label ?? fieldLabel(control.field, tagsBySlug);
        if (control.kind === "time") {
          return (
            <ControlRow key={key} label={label}>
              <TimeControl
                label={label}
                presets={control.presetMinutes ?? DEFAULT_TIME_PRESETS}
                minutes={typeof value === "number" ? value : 0}
                onChange={(minutes) => onControlChange(key, minutes)}
              />
            </ControlRow>
          );
        }
        const kind = fieldKindFor(control.field, tagsBySlug);
        const values = Array.isArray(value) ? value : [];
        const multiple = control.multiple ?? true;
        return (
          <ControlRow key={key} label={label}>
            {kind.valueType === "enum" || kind.valueType === "boolean" ? (
              <ChipsControl
                options={optionsFor(control)}
                values={values}
                multiple={multiple}
                onChange={(next) => onControlChange(key, next)}
              />
            ) : (
              <ValuesPicker
                label={label}
                options={optionsFor(control)}
                values={values}
                multiple={multiple}
                onChange={(next) => onControlChange(key, next)}
              />
            )}
          </ControlRow>
        );
      })}
      {fieldFilters.map((filter) => (
        <ControlRow key={filter.key} label={filter.label}>
          <FieldValuesEditor
            label={filter.label}
            kind={filter.kind}
            options={fieldOptionsFor(filter.key)}
            values={chosenValuesFor(filter.key)}
            onChange={(values) => onFieldValuesChange(filter.key, values)}
          />
        </ControlRow>
      ))}
      <Typography sx={{ fontSize: "0.75rem", color: zinc[500] }}>
        Click a column header to filter by that column; right-click a row to keep only, or exclude, rows sharing one of
        its values.
      </Typography>
    </>
  );
}

/**
 * The "Filter" button in the header and the popover it opens: what the person is asked to acknowledge, always first,
 * then the query's controls, one under another, each rendered by its field's kind, then an editor for every field that
 * narrows rows yet has no column, since a column's header is where a field is otherwise filtered and these (the
 * acknowledgement layer, say) have none. Values beyond an enum or a boolean are picked in the column header's checklist,
 * opened over this popover and applied with Apply. The button names how many narrowings are active, in the brand tint;
 * the strip under the header spells them out and clears them. The column editors write ad-hoc conditions, and so do
 * the field editors here, so a field's chosen values read the same from either.
 */
export default React.memo(function DispatchFilterMenu({
  controls,
  tagsBySlug,
  controlValues,
  onControlChange,
  optionsFor,
  fieldFilters,
  fieldOptionsFor,
  chosenValuesFor,
  onFieldValuesChange,
  requiresAcknowledgement,
  activeCount,
  hidden,
}: DispatchFilterMenuProps) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const filterWord = activeCount === 1 ? "filter" : "filters";
  return (
    <>
      <ButtonBase
        ref={anchorRef}
        data-sb={TestIds.dispatch.filterMenu}
        aria-label="Filter"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        sx={filterButtonSx(activeCount, hidden, open)}
      >
        {activeCount > 0 && hidden ? (
          <>
            <VisibilityOffIcon sx={{ fontSize: 18 }} />
            {activeCount} {filterWord} hidden
          </>
        ) : (
          <>
            <FilterListIcon sx={{ fontSize: 18 }} />
            {activeCount > 0 ? `${activeCount} ${filterWord}` : "Filter"}
          </>
        )}
      </ButtonBase>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            "data-sb": TestIds.dispatch.filterPopover,
            sx: {
              width: 360,
              maxHeight: "70vh",
              overflowY: "auto",
              bgcolor: zinc[800],
              border: `1px solid ${zinc[600]}`,
              p: 1.5,
              mt: 0.5,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            },
          } as React.ComponentProps<typeof Popover>["PaperProps"],
        }}
      >
        <FilterMenuBody
          controls={controls}
          tagsBySlug={tagsBySlug}
          controlValues={controlValues}
          onControlChange={onControlChange}
          optionsFor={optionsFor}
          fieldFilters={fieldFilters}
          fieldOptionsFor={fieldOptionsFor}
          chosenValuesFor={chosenValuesFor}
          onFieldValuesChange={onFieldValuesChange}
          requiresAcknowledgement={requiresAcknowledgement}
        />
      </Popover>
    </>
  );
});
